import { useEffect, useRef, type ReactNode } from 'react';
import { Building2, ChevronRight, ClipboardCheck, Settings2, Users, X } from 'lucide-react';
import { Button } from '@kop/ui';
import { openMobileInstallDialog } from './mobile-install-dialog';

export type SiteAdministrationSection = Readonly<{ id: string; label: string; group: string }>;

const focusable =
  'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

const groupIcon = {
  '사업장 설정': Building2,
  '점검 설정': ClipboardCheck,
  '사용자 설정': Users,
  '운영 지원': Settings2,
} as const;

export interface SiteAdministrationDialogProps {
  sections: readonly SiteAdministrationSection[];
  activeSectionId: string;
  onSelectSection(sectionId: string): void;
  onPrefetchSection?(sectionId: string): void;
  onClose(): void;
  children: ReactNode;
}

/**
 * Site administration over the current screen.
 *
 * The panel is the shell's, not a feature's: it holds the section list and the
 * selected section's ledger side by side, and the ledger is passed in as
 * children so moving between sections re-renders only the content region. The
 * panel itself, its scroll position and its focus trap survive the move.
 *
 * Unlike the setup dialog this is not a gate. The close control, Escape and a
 * backdrop click all dismiss it, and the workspace underneath stays live.
 */
export function SiteAdministrationDialog({
  sections,
  activeSectionId,
  onSelectSection,
  onPrefetchSection,
  onClose,
  children,
}: SiteAdministrationDialogProps) {
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.querySelector<HTMLElement>(focusable)?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const items = [...panelRef.current.querySelectorAll<HTMLElement>(focusable)].filter(
        (item) => item.offsetParent !== null,
      );
      if (!items.length) return;
      const first = items[0]!;
      const last = items.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [onClose]);

  const active = sections.find((section) => section.id === activeSectionId) ?? sections[0];
  const groups = [...new Set(sections.map((section) => section.group))];

  return (
    <div className="site-admin-backdrop" onMouseDown={onClose}>
      <section
        ref={panelRef}
        className="site-admin-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-admin-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="site-admin-dialog-head">
          <h2 id="site-admin-dialog-title">사업장 관리</h2>
          <Button
            variant="quiet"
            size="compact"
            icon={<X size={20} />}
            aria-label="사업장 관리 닫기"
            title="닫기"
            onClick={onClose}
          />
        </header>
        <div className="site-admin-dialog-body">
          <nav className="site-admin-dialog-sections" aria-label="사업장 관리 항목">
            {groups.map((group) => (
              <div className="site-admin-dialog-section-group" data-group={group} key={group}>
                <strong>
                  {(() => {
                    const Icon = groupIcon[group as keyof typeof groupIcon] ?? Settings2;
                    return <Icon size={13} aria-hidden="true" />;
                  })()}
                  {group}
                </strong>
                {sections
                  .filter((section) => section.group === group)
                  .map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      data-section-id={section.id}
                      className={section.id === active?.id ? 'active' : ''}
                      aria-current={section.id === active?.id ? 'page' : undefined}
                      onPointerEnter={() => onPrefetchSection?.(section.id)}
                      onFocus={() => onPrefetchSection?.(section.id)}
                      onClick={() => onSelectSection(section.id)}
                    >
                      <span>{section.label}</span>
                      <ChevronRight size={15} />
                    </button>
                  ))}
              </div>
            ))}
            <button
              type="button"
              className="site-admin-dialog-mobile-download"
              onClick={openMobileInstallDialog}
            >
              <Settings2 size={15} aria-hidden="true" />
              <span>모바일 앱 설치</span>
              <ChevronRight size={15} aria-hidden="true" />
            </button>
          </nav>
          <div
            className="site-admin-dialog-content"
            role="region"
            aria-label={active?.label ?? '사업장 관리'}
          >
            {children}
          </div>
        </div>
      </section>
    </div>
  );
}
